import { doRequest, applyFilters } from "./helpers";
import AuthService from "./Auth";
import useAuthStore from "../stores/useAuthStore";

export default class UserService {
  static async search(query) {
    return doRequest("GET", "/user/search/" + query);
  }

  static async getUser(userId, withDeleted = false) {
    let path = `/user/${userId}`;
    if (withDeleted) {
      path = applyFilters({ with_deleted: true }, path);
    }
    return doRequest("GET", path);
  }

  static async getUsersForFilters(filters = null) {
    let path = "/user/for_filters";
    path = applyFilters(filters, path);
    return doRequest("GET", path);
  }

  static async getUserSettings() {
    return doRequest("GET", "/user/settings");
  }

  static async editUserSettings(data) {
    let account = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      title: data.title,
      department: data.department,
      mobile_phone: data.mobile,
      time_zone_utc: data.timeZone,
      time_zone_name: data.timeZoneName,
      region_id: data.region,
      preferred_name: data.preferredName,
      attachment: data.attachment,
      remove_profile_picture: data.removeProfilePicture,
      old_password: data.oldPassword,
      new_password: data.newPassword,
      phone_numbers: data.phoneNumbers,
    };
    return doRequest("PUT", "/user/settings", {
      data: { settings: { account: account } },
    });
  }

  static async updateUserTableSettings(table_name, setting_name, data) {
    // update store
    let user = structuredClone(useAuthStore.getState().userAuth);
    if (user.table_settings === undefined || user.table_settings == null) {
      user.table_settings = {};
    }
    if (!user.table_settings[table_name]) {
      user.table_settings[table_name] = {};
    }
    user.table_settings[table_name][setting_name] = data;
    useAuthStore.getState().setUserAuth(user);
    // update DB
    let preferences = { tables: user.table_settings };
    return doRequest("PUT", "/user/settings", {
      data: { settings: { preferences: preferences } },
    });
  }

  static async updateUserPipelineSettings(campaignId, settingName, data) {
    let user = structuredClone(useAuthStore.getState().userAuth);
    if (!user.pipeline_settings) {
      user.pipeline_settings = {};
    }
    if (!user.pipeline_settings[campaignId]) {
      user.pipeline_settings[campaignId] = {};
    }

    user.pipeline_settings[campaignId][settingName] = data;
    useAuthStore.getState().setUserAuth(user);
    let preferences = { pipelines: user.pipeline_settings };
    return doRequest("PUT", "/user/settings", {
      data: { settings: { preferences: preferences } },
    });
  }

  static async updateUserSidebarSettings(sidebarName, settingName, data) {
    // update store
    let user = structuredClone(useAuthStore.getState().userAuth);
    if (user.sidebar_settings === undefined || user.sidebar_settings === null) {
      user.sidebar_settings = {};
    }
    if (!user.sidebar_settings[sidebarName]) {
      user.sidebar_settings[sidebarName] = {};
    }
    user.sidebar_settings[sidebarName][settingName] = data;
    useAuthStore.getState().setUserAuth(user);
    // update DB
    let preferences = { sidebars: user.sidebar_settings };
    return doRequest("PUT", "/user/settings", {
      data: { settings: { preferences } },
    });
  }

  static async updateShowDisabledTags(value) {
    const sidebars = {
      ...useAuthStore.getState().userAuth.sidebar_settings,
      show_disabled_tags: value,
    };
    // The store is updated only after the save succeeds, so a failed request
    // leaves every switch showing the value that is actually stored. It is
    // re-read after the request so settings saved meanwhile are not reverted.
    await doRequest("PUT", "/user/settings", {
      data: { settings: { preferences: { sidebars } } },
    });
    const user = structuredClone(useAuthStore.getState().userAuth);
    user.sidebar_settings = {
      ...user.sidebar_settings,
      show_disabled_tags: value,
    };
    useAuthStore.getState().setUserAuth(user);
  }

  static async resetTable(tableName) {
    const user = structuredClone(useAuthStore.getState().userAuth);
    if (user?.table_settings[tableName]) {
      user.table_settings[tableName] = {};
    }

    useAuthStore.getState().setUserAuth(user);
    const preferences = {
      tables: { ...user.table_settings },
      pipelines: { ...user.pipeline_settings },
      sidebars: { ...user.sidebar_settings },
    };
    preferences.tables[tableName] = {};

    return doRequest("PUT", "/user/settings", {
      data: { settings: { preferences: preferences } },
    });
  }

  static async resetPreferences() {
    // update store
    let user = structuredClone(useAuthStore.getState().userAuth);
    user.table_settings = {
      target_companies: {},
      campaigns: {},
    };
    useAuthStore.getState().setUserAuth(user);
    // update DB
    let preferences = {
      tables: {
        target_companies: {},
        campaigns: {},
      },
      pipelines: {},
    };
    return doRequest("PUT", "/user/settings", {
      data: { settings: { preferences: preferences } },
    });
  }

  static async getUsers() {
    return doRequest("GET", "/user/");
  }

  static async getCallers(filters = null) {
    let path = "/user/callers";
    path = applyFilters(filters, path);
    return doRequest("GET", path);
  }

  static async getUserAuditLogNotificationSettings() {
    return doRequest("GET", "/user/settings/notifications");
  }

  static async getUserAuditLogNotificationSetting(category, action) {
    return doRequest(
      "GET",
      `/user/settings/notifications/${category}/${action}`
    );
  }

  static async editUserAuditLogNotificationSetting(category, action, status) {
    return doRequest(
      "PUT",
      `/user/settings/notifications/${category}/${action}`,
      { data: { status } }
    );
  }

  static async editUserAuditLogNotificationSettings(settingList) {
    return doRequest("PUT", `/user/settings/notifications/`, {
      data: { setting_list: settingList },
    });
  }

  static isAdmin() {
    if (!AuthService.isLoggedIn()) {
      return false;
    }
    const user = useAuthStore.getState().userAuth;
    return user?.role?.id === -1;
  }

  static async getUsersAsAdmin(filters) {
    let path = `/admin/user`;
    path = applyFilters(filters, path);
    return doRequest("GET", path);
  }

  static async getUserAsAdmin(userId) {
    return doRequest("GET", `/admin/user/${userId}`);
  }

  static async createUser(data) {
    const {
      email,
      fullName,
      title,
      phoneNumbers,
      role,
      region,
      groupId,
      customFields,
      ssoBypass,
    } = data;
    return doRequest("POST", "/admin/user", {
      data: {
        email,
        full_name: fullName,
        phone_numbers: phoneNumbers,
        role_id: role,
        title,
        region_id: region,
        ...(groupId ? { group_id: groupId } : {}),
        custom_fields: customFields,
        sso_bypass: ssoBypass,
      },
    });
  }

  static async updateUser(userId, data) {
    const {
      email,
      fullName,
      title,
      phoneNumbers,
      role,
      region,
      groupId,
      rolesOnly,
      customFields,
      ssoBypass,
    } = data;
    return doRequest("PUT", `/admin/user/${userId}`, {
      data: {
        email,
        full_name: fullName,
        phone_numbers: phoneNumbers,
        role_id: role,
        title,
        region_id: region,
        ...(groupId ? { group_id: groupId } : {}),
        roles_only: rolesOnly,
        custom_fields: customFields,
        sso_bypass: ssoBypass,
      },
    });
  }

  static async getUserStatus(userId) {
    return doRequest("GET", `/agent_status/${userId}`);
  }

  static async updateUserStatus(userId, data) {
    return doRequest("PUT", `/agent_status/${userId}`, {
      data: {
        ...data,
      },
    });
  }

  static async deleteUser(userId) {
    return doRequest("DELETE", `/admin/user/${userId}`);
  }

  static async resetPassword(userId) {
    return doRequest("POST", `/admin/user/${userId}/reset_password`);
  }

  static async deactivateUser(userId) {
    return doRequest("POST", `/admin/user/${userId}/deactivate`);
  }

  static async reactivateUser(userId) {
    return doRequest("POST", `/admin/user/${userId}/reactivate`);
  }

  static async forceLogout(userId) {
    return doRequest("POST", `/admin/user/${userId}/force_logout`);
  }

  static async getCustomFields() {
    let path = `/user/custom_fields`;
    return doRequest("GET", path);
  }
}
